import {
  Checkbox,
  CircularProgress,
  Icon,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import { Alert, Autocomplete } from '@material-ui/lab'
import { ManagementContext } from 'context'
import { createTaxPlanCustomer, deleteTaxPlanCustomer, TAXPLAN_TABLE_RETENTION_COLUMNS } from 'lib'
import React from 'react'
import { ICustomer, IMessageConfig, ITaxPlan, ITaxPlanCustomer } from 'types'

interface Props {
  onClose: () => void
  customer?: ICustomer
  taxPlanCustomer: ITaxPlanCustomer[]
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const TaxPlanCustomer = (props: Props) => {
  const { onClose, customer } = props

  const { taxPlans, taxTypes } = React.useContext(ManagementContext)

  const [loading, setLoading] = React.useState<boolean>(false)
  const [taxPlanCustomer, setTaxPlanCustomer] = React.useState(props.taxPlanCustomer ?? [])
  const [messageConfig, setMessageConfig] = React.useState<IMessageConfig>({
    open: false,
    message: '',
    severity: 'info',
  })

  const inputRef = React.useRef<any>(null)

  const getTaxPlanIds = React.useCallback(() => {
    return taxPlanCustomer.map((t) => t.taxPlanId)
  }, [taxPlanCustomer])

  const handleAdd = async (taxPlan: ITaxPlan) => {
    try {
      setLoading(true)
      const result = await createTaxPlanCustomer({
        customerId: customer?.customerId,
        taxPlanId: taxPlan.taxPlanId,
      })
      setTaxPlanCustomer((current) => [...current, result])
      setLoading(false)
      inputRef.current?.focus()
      setMessageConfig({ open: true, message: 'Plan de impuestos asignado!', severity: 'success' })
    } catch (error) {
      setLoading(false)
      setMessageConfig({
        open: true,
        message: 'No se pudo agregar el plan de impuestos',
        severity: 'error',
      })
    }
  }

  const handleDelete = async (taxPlan: ITaxPlan) => {
    try {
      setLoading(true)
      const taxPlanCustomerId = taxPlanCustomer.find(
        (t) => t.taxPlanId === taxPlan.taxPlanId
      )?.taxPlanCustomerId
      await deleteTaxPlanCustomer(taxPlanCustomerId)
      setTaxPlanCustomer((current) =>
        current.filter((c) => c.taxPlanCustomerId !== taxPlanCustomerId)
      )
      setLoading(false)
      inputRef.current?.focus()
      setMessageConfig({ open: true, message: 'Plan de impuestos eliminado!', severity: 'success' })
    } catch (error) {
      setLoading(false)
      setMessageConfig({
        open: true,
        message: 'No se pudo eliminar el plan de impuestos',
        severity: 'error',
      })
    }
  }

  return (
    <div className="p-4 overflow-hidden" style={{ width: 550 }}>
      <div className="flex items-center justify-between">
        <div>
          <Typography variant="body2" style={{ fontWeight: 600, letterSpacing: 0.5 }}>
            {'Administrar plan de impuestos'}
          </Typography>
          <Typography
            color="textSecondary"
            variant="caption"
            style={{ fontWeight: 600, letterSpacing: 0.5 }}
          >
            {customer?.companyName}
          </Typography>
        </div>
        <IconButton disabled={loading} onClick={onClose} size="small">
          <Icon fontSize="small">close</Icon>
        </IconButton>
      </div>
      <form>
        <div className="flex items-center my-1">
          <Autocomplete
            multiple
            options={taxPlans.filter((t) => t.forCustomers)} 
            disableCloseOnSelect
            disabled={loading}
            getOptionLabel={(tax) => `${tax.name}`}
            limitTags={2}
            fullWidth
            value={taxPlans.filter((t) => getTaxPlanIds().includes(t.taxPlanId))}
            openOnFocus
            disableClearable
            size="small"
            style={{ marginTop: 20 }}
            ChipProps={{ size: 'small', style: { maxWidth: 90 } }}
            onChange={(_, _values, reason, value) => {
              if (reason === 'clear' || reason === 'remove-option') {
                if (value?.option) {
                  handleDelete(value.option)
                }
              } else if (value?.option) {
                handleAdd(value.option)
              }
            }}
            renderOption={(tax, { selected }) => {
              return (
                <React.Fragment>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8, height: 50 }}
                    checked={selected}
                  />
                  {`${tax.name}`}
                </React.Fragment>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={inputRef}
                variant="outlined"
                fullWidth
                label={'Selecciona para agregar/eliminar'}
              />
            )}
          />
        </div>
      </form>
      <div
        className="w-full overflow-y-auto py-1 mt-4 relative"
        style={{ height: '50vh', maxHeight: '50vh' }}
      >
        {taxPlanCustomer.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center">
            <Typography align="center" color="textSecondary" className="px-6" variant="h6">
              {'No se han agregado planes de impuestos a este proveedor.'}
            </Typography>
          </div>
        ) : (
          <React.Fragment>
            <Typography align="center" color="textSecondary" className="pb-4" variant="caption">
              {'Agregados:'}
            </Typography>
            {taxPlans
              .filter((t) => getTaxPlanIds().includes(t.taxPlanId))
              .map((t) => (
                <div className="my-1 flex items-center flex-col w-full">
                  <div className="flex items-center justify-between w-full">
                    <Typography>{`${t.name}`}</Typography>
                    <div className="flex items-center">
                      <Tooltip arrow title="Eliminar plan de impuestos">
                        <IconButton disabled={loading} onClick={() => handleDelete(t)} size="small">
                          <Icon fontSize="small">close</Icon>
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="w-full py-2">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {TAXPLAN_TABLE_RETENTION_COLUMNS.map((headerCell) => (
                            <TableCell padding="none">{headerCell.label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {t.taxCodes.map((row) => {
                          return (
                            <TableRow key={row.taxId} hover role="checkbox" tabIndex={-1} selected>
                              <TableCell padding="none" align="left">
                                {
                                  taxTypes.find(
                                    (t) => t.taxId.toString() === row.taxTypeId.toString()
                                  )?.name
                                }
                              </TableCell>
                              <TableCell padding="none" align="left">
                                {row.taxCode}
                              </TableCell>
                              <TableCell padding="none" align="left">
                                {row.description}
                              </TableCell>
                              <TableCell padding="none" align="left">{`${row.percentage.toFixed(
                                2
                              )}%`}</TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
          </React.Fragment>
        )}
        {loading && (
          <div className="absolute mb-2 mr-2 flex items-center bottom-0 right-0">
            <CircularProgress thickness={10} size={22} color="primary" />
          </div>
        )}
      </div>
      <Snackbar
        open={messageConfig.open}
        autoHideDuration={6000}
        onClose={() => setMessageConfig({ ...messageConfig, open: false })}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Alert
          variant="filled"
          onClose={() => setMessageConfig({ ...messageConfig, open: false })}
          severity={messageConfig.severity}
        >
          {messageConfig.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default TaxPlanCustomer
